import React, { useState } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// Icons
// ==========================================

export const OtherAgentIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8b5cf6"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z" />
    <path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z" />
    <circle cx="12" cy="6" r="1" />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ==========================================
// Framework Definitions & Metadata
// ==========================================

export type SupportedFramework =
  | "langchain"
  | "langgraph"
  | "llamaindex"
  | "crewai"
  | "autogen"
  | "openai-agents"
  | "google-adk"
  | "custom-python";

export interface FrameworkMetadata {
  id: SupportedFramework;
  name: string;
  description: string;
  installCommand: string;
  defaultFile: string;
  defaultFunction: string;
}

export const FRAMEWORKS: FrameworkMetadata[] = [
  {
    id: "langchain",
    name: "LangChain",
    description: "Chains, Runnables, and AgentExecutors with invoke() execution",
    installCommand: "pip install langchain-core langchain-openai",
    defaultFile: "file://./langchain_agent.py",
    defaultFunction: "invoke",
  },
  {
    id: "langgraph",
    name: "LangGraph",
    description: "Stateful, cyclic multi-actor agent workflows and graph orchestration",
    installCommand: "pip install langgraph langchain-openai",
    defaultFile: "file://./langgraph_agent.py",
    defaultFunction: "invoke",
  },
  {
    id: "llamaindex",
    name: "LlamaIndex",
    description: "Data framework for LLM applications and agentic query engines",
    installCommand: "pip install llama-index llama-index-llms-openai",
    defaultFile: "file://./llamaindex_agent.py",
    defaultFunction: "call_api",
  },
  {
    id: "crewai",
    name: "CrewAI",
    description: "Role-playing, autonomous multi-agent orchestration framework",
    installCommand: "pip install crewai",
    defaultFile: "file://./crewai_agent.py",
    defaultFunction: "call_api",
  },
  {
    id: "autogen",
    name: "AutoGen",
    description: "Microsoft conversational multi-agent workflow framework",
    installCommand: "pip install pyautogen",
    defaultFile: "file://./autogen_agent.py",
    defaultFunction: "call_api",
  },
  {
    id: "openai-agents",
    name: "OpenAI Agents SDK",
    description: "OpenAI Agents SDK multi-agent framework and runner workflows",
    installCommand: "pip install openai-agents",
    defaultFile: "file://./openai_agent.py",
    defaultFunction: "call_api",
  },
  {
    id: "google-adk",
    name: "Google ADK",
    description: "Google Agent Development Kit modular multi-agent framework",
    installCommand: "pip install google-adk google-genai",
    defaultFile: "file://./google_adk_agent.py",
    defaultFunction: "call_api",
  },
  {
    id: "custom-python",
    name: "Generic Python Agent",
    description: "Custom Python agent script conforming to call_api or callable contract",
    installCommand: "pip install -r requirements.txt",
    defaultFile: "file://./agent.py",
    defaultFunction: "call_api",
  },
];

// ==========================================
// Types & Defaults
// ==========================================

export interface OtherAgentTargetConfigValues {
  framework: SupportedFramework;
  entryPointType: "file" | "app_id" | "command" | "endpoint";
  filePath: string;
  functionName: string;
  appId: string;
  endpoint: string;
  command: string;
  pythonExecutable: string;
  workingDir: string;
  configJson: string;
  env: Record<string, string>;
  timeout: number | string;
}

export const DEFAULT_OTHER_AGENT_CONFIG: OtherAgentTargetConfigValues = {
  framework: "langchain",
  entryPointType: "file",
  filePath: "file://./agent.py",
  functionName: "invoke",
  appId: "my_langchain_app",
  endpoint: "http://localhost:8000/agent",
  command: "exec:python /path/to/agent.py",
  pythonExecutable: "python3",
  workingDir: "",
  configJson: "{}",
  env: {},
  timeout: 60,
};

// ==========================================
// Props
// ==========================================

export interface OtherAgentTargetConfigurationProps {
  targetName?: string;
  values: OtherAgentTargetConfigValues;
  onChange: (updates: Partial<OtherAgentTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => void;
  isTestingConnection?: boolean;
  testResult?: {
    success: boolean;
    status?: number | null;
    message?: string;
    response_time?: string;
    sample_response?: string;
    sample_output?: string;
    output?: string;
    error_message?: string | null;
  } | null;
}

// ==========================================
// Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const cardStyle = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .theme--dark & {
    background: #0f172a;
    border-color: #334155;
  }
`;

const sectionHeadingStyle = css`
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .theme--dark & {
    color: #f8fafc;
  }
`;

const fieldGroupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const labelStyle = css`
  font-size: 0.88rem;
  font-weight: 600;
  color: #334155;

  .theme--dark & {
    color: #cbd5e1;
  }
`;

const inputStyle = css`
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.9rem;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.15);
  }

  .theme--dark & {
    background: #1e293b;
    border-color: #475569;
    color: #f8fafc;
  }
`;

const textareaStyle = css`
  ${inputStyle};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
  resize: vertical;
  min-height: 80px;
`;

const helperTextStyle = css`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 4px;

  .theme--dark & {
    color: #94a3b8;
  }
`;

const frameworkGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;

const frameworkCard = (active: boolean) => css`
  padding: 12px 14px;
  border-radius: 8px;
  border: 1.5px solid ${active ? "#8b5cf6" : "#e2e8f0"};
  background: ${active ? "rgba(139, 92, 246, 0.06)" : "#ffffff"};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: #8b5cf6;
    background: rgba(139, 92, 246, 0.04);
  }

  .theme--dark & {
    background: ${active ? "rgba(139, 92, 246, 0.15)" : "#1e293b"};
    border-color: ${active ? "#8b5cf6" : "#334155"};
  }
`;

const infoBoxStyle = css`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;

  .theme--dark & {
    background: #1e293b;
    border-color: #334155;
  }
`;

const diagnosticsBox = (success: boolean) => css`
  background: ${success ? "rgba(16, 185, 129, 0.05)" : "rgba(239, 68, 68, 0.05)"};
  border: 1px solid ${success ? "#a7f3d0" : "#fecaca"};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  .theme--dark & {
    background: ${success ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)"};
    border-color: ${success ? "#065f46" : "#991b1b"};
  }
`;

export const OtherAgentTargetConfiguration: React.FC<OtherAgentTargetConfigurationProps> = ({
  targetName = "Other Agent Framework",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult,
}) => {
  const currentFw = values.framework || "langchain";
  const activeFwMeta = FRAMEWORKS.find((f) => f.id === currentFw) || FRAMEWORKS[0];

  const handleFrameworkSelect = (fwId: SupportedFramework) => {
    const meta = FRAMEWORKS.find((f) => f.id === fwId);
    onChange({
      framework: fwId,
      filePath: meta?.defaultFile || values.filePath,
      functionName: meta?.defaultFunction || values.functionName,
    });
  };

  return (
    <div css={pageWrapperStyle}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <Heading level={2} style={{ margin: 0, fontSize: "1.45rem", fontWeight: 800 }}>
            Configure {targetName}
          </Heading>
          <p css={helperTextStyle} style={{ margin: "4px 0 0" }}>
            Connect and execute active red-team scans against LangChain, LangGraph, LlamaIndex, CrewAI, AutoGen, or custom Python agents.
          </p>
        </div>
        {renderModeSwitcher && <div>{renderModeSwitcher}</div>}
      </div>

      {/* Main Configuration Card */}
      <div css={cardStyle}>
        {/* Framework Selection */}
        <div css={sectionHeadingStyle}>
          <OtherAgentIcon />
          <span>Select Agent Framework</span>
        </div>

        <div css={frameworkGrid}>
          {FRAMEWORKS.map((fw) => {
            const active = currentFw === fw.id;
            return (
              <button
                key={fw.id}
                type="button"
                css={frameworkCard(active)}
                onClick={() => handleFrameworkSelect(fw.id)}
              >
                <div style={{ fontWeight: 700, fontSize: "0.92rem", color: active ? "#7c3aed" : "#0f172a" }}>
                  {fw.name}
                </div>
                <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: "4px", lineHeight: "1.3" }}>
                  {fw.description}
                </div>
              </button>
            );
          })}
        </div>

        {/* Framework Info & Pip Install */}
        <div css={infoBoxStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
            <div>
              <strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>{activeFwMeta.name} Integration</strong>
              <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: "2px" }}>
                {activeFwMeta.description}
              </div>
            </div>
            <div style={{ background: "#e2e8f0", padding: "6px 12px", borderRadius: "6px", fontFamily: "ui-monospace, monospace", fontSize: "0.8rem", color: "#334155" }}>
              {activeFwMeta.installCommand}
            </div>
          </div>
        </div>

        {/* Entry Point Details */}
        <div css={sectionHeadingStyle}>
          <span>Agent Entry Point</span>
        </div>

        <div css={fieldGroupStyle}>
          <label css={labelStyle}>Python File Path *</label>
          <input
            type="text"
            css={inputStyle}
            value={values.filePath || ""}
            onChange={(e) => onChange({ filePath: e.target.value })}
            placeholder="file://./agent.py or ./my_framework_agent.py"
          />
          <span css={helperTextStyle}>
            Path to the Python file containing your {activeFwMeta.name} agent or runnable.
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div css={fieldGroupStyle}>
            <label css={labelStyle}>Invocation Function / Method</label>
            <input
              type="text"
              css={inputStyle}
              value={values.functionName || "call_api"}
              onChange={(e) => onChange({ functionName: e.target.value })}
              placeholder={activeFwMeta.defaultFunction}
            />
            <span css={helperTextStyle}>
              Function or method name called to execute prompt (e.g. call_api, invoke, run, query).
            </span>
          </div>

          <div css={fieldGroupStyle}>
            <label css={labelStyle}>Python Executable / Virtualenv</label>
            <input
              type="text"
              css={inputStyle}
              value={values.pythonExecutable || "python3"}
              onChange={(e) => onChange({ pythonExecutable: e.target.value })}
              placeholder="python3 or /path/to/venv/bin/python"
            />
          </div>
        </div>

        <div css={fieldGroupStyle}>
          <label css={labelStyle}>Working Directory (Optional)</label>
          <input
            type="text"
            css={inputStyle}
            value={values.workingDir || ""}
            onChange={(e) => onChange({ workingDir: e.target.value })}
            placeholder="./ or /path/to/project"
          />
        </div>

        <div css={fieldGroupStyle}>
          <label css={labelStyle}>Configuration (JSON)</label>
          <textarea
            css={textareaStyle}
            value={values.configJson || "{}"}
            onChange={(e) => onChange({ configJson: e.target.value })}
            rows={3}
          />
        </div>

        <div css={fieldGroupStyle}>
          <label css={labelStyle}>Execution Timeout (seconds)</label>
          <input
            type="number"
            css={inputStyle}
            style={{ width: "160px" }}
            value={values.timeout || 60}
            onChange={(e) => onChange({ timeout: Number(e.target.value) || 60 })}
            min={1}
            max={120}
          />
        </div>

        {/* Connection Testing Button & Results */}
        <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e2e8f0" }}>
          <Button
            variant="secondary"
            onClick={onTestConnection}
            disabled={isTestingConnection}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}
          >
            <WifiIcon />
            {isTestingConnection ? "Testing Agent Connection..." : "Test Agent Connection"}
          </Button>

          {testResult && (
            <div css={diagnosticsBox(Boolean(testResult.success))}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, color: testResult.success ? "#059669" : "#dc2626" }}>
                {testResult.success ? <CheckIcon /> : <CrossIcon />}
                <span>{testResult.success ? "Connection Successful" : "Connection Failed"}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "10px", fontSize: "0.85rem" }}>
                {testResult.status !== undefined && testResult.status !== null && (
                  <div><strong>Status Code:</strong> {testResult.status}</div>
                )}
                {testResult.response_time && (
                  <div><strong>Response Time:</strong> {testResult.response_time}</div>
                )}
              </div>
              {testResult.error_message && (
                <div style={{ marginTop: "10px", fontSize: "0.85rem", color: "#b91c1c" }}>
                  <strong>Diagnostics:</strong> {testResult.error_message}
                </div>
              )}
              {(testResult.sample_response || testResult.sample_output || testResult.output) && (
                <div style={{ marginTop: "12px", borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: "8px" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#334155", marginBottom: "4px" }}>
                    Sample Agent Response:
                  </div>
                  <pre
                    style={{
                      margin: 0,
                      padding: "8px 12px",
                      background: "rgba(0,0,0,0.04)",
                      borderRadius: "6px",
                      fontSize: "0.82rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: "150px",
                      overflowY: "auto",
                      fontFamily: "ui-monospace, monospace",
                    }}
                  >
                    {testResult.sample_response || testResult.sample_output || testResult.output}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      {!hideBottomBar && (
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "12px" }}>
          <Button variant="secondary" onClick={onBack}>
            &lt; Back
          </Button>
          <Button variant="primary" onClick={onNext}>
            Next &gt;
          </Button>
        </div>
      )}
    </div>
  );
};
export default OtherAgentTargetConfiguration;
